import React, { useContext, useEffect, useState } from 'react';

import _ from 'lodash';

import { ImagePreview } from './ImagePreview';
import CountryFlag from '../common/CountryFlag';
import LegoAdminPageContext from '../../pages/legoAdminPageContext';
import { IconButton } from '../common/IconButton';
import ImageClusterGallery from './ImageClusterGallery';
import useGetCachedDocIdBatched from '../common/react-hooks/useGetCachedDocIdBatched';
import { Icon } from '../common/Icon';
import BadgeColorHash from '../common/BadgeColorHash';


function vehicleToString(vehicle) {
  return _.values(_.omit(vehicle, 'trimId')).map(v => _.isObject(v) ? _.values(v).join(' ') : v).join(' ');
}

function ImageClusterPreview({ clusterId, size = '50', maxImages = 10, filterByYear = undefined, highlightYear = undefined, highlightCountries = undefined, highlightImageIds = undefined, preloadedCluster = undefined, sortByYear = undefined, previewLeft = false }) {
  const maxShown = maxImages;
  const { page } = useContext(LegoAdminPageContext);

  const cluster = preloadedCluster || useGetCachedDocIdBatched('services/data/image-clusters', 20)(clusterId);

  const openExtendedCluster = (e) => {
    e.stopPropagation();
    page.openModal(<ImageClusterGallery clusterId={clusterId} filterByYear={filterByYear} />,
      {
        title: <>Cluster <BadgeColorHash id={clusterId} /> - {vehicleToString(cluster.context)}</>,
        fullScreenFixed: true,
        oPadding: true
      });
  };

  const highlightIdSet = highlightImageIds?.length ? new Set(highlightImageIds.map(String)) : null;

  if (cluster) {
    let { images } = cluster;
    let shownCount = 0;
    let filteredCount = images.length;
    if (_.isNumber(filterByYear)) {
      images = _.filter(images, i => i.vehicle.year === filterByYear);
      filteredCount = images.length;
    }

    if (sortByYear) {
      images = _.orderBy(images, 'vehicle.year');
    } else if (highlightYear) {
      images = _.orderBy(images, i => Math.abs(highlightYear - i.vehicle?.year));
    }

    if (highlightCountries) {
      images = _.orderBy(images, i => highlightCountries.includes(i.vehicle?.countryCode) ? 0 : 1);
    }

    if (highlightIdSet) {
      images = _.orderBy(images, i => highlightIdSet.has(String(i.id)) ? 0 : 1);
    }

    images = images.slice(0, maxShown);

    const sizeProp = size === '100' ? { img100: true } : size === '200' ? { img200: true } : { img50: true };

    return <span className={`border bg-light rounded p-1 inline-block align-middle`}>

      <span style={{ minHeight: '72px' }} className={`d-inline-flex align-items-top`}>

        {_.map(images, ({ id, url, vehicle }, j) => {
          const vehicleString = vehicleToString(vehicle);

          let highlightClass = '';
          if (highlightYear && vehicle.year !== highlightYear)
            highlightClass = 'translucent';

          if (highlightCountries?.length && !highlightCountries.includes(vehicle.countryCode))
            highlightClass = 'translucent';

          if (highlightIdSet)
            highlightClass = highlightIdSet.has(String(id)) ? 'border border-primary' : 'translucent';


          shownCount++;
          return <span className={`inline-block`} key={id + '-' + j} title={vehicleString}>
            <ImagePreview {...sizeProp} cover key={id} url={url || ''} imageId={id} left={previewLeft} className={`mr-1 bg-light ${highlightClass}`} zoom context={vehicle} />

            <div className={'small zoom-90'}>
              <CountryFlag countryCode={(vehicle.countryCode || '').toLowerCase()} />
              <span className={'ml-1'}>{vehicle.year}</span>
            </div>
          </span>;
        })}

        <IconButton className={'align-self-center p-0 small text-center'} onClick={(e) => openExtendedCluster(e)}>
          <Icon icon={'search'} className={'align-self-center text-center'} />
          {cluster.images.length > shownCount ? <div>{cluster.images.length - shownCount}<br/>more</div> : null}
        </IconButton>
      </span>
    </span>;
  } else if (cluster === false) {
    return <span className={'align-items-top text-danger'}>_id "<code>{clusterId}</code>" NOT FOUND</span>;
  } else {
    return <span className={`border bg-light rounded p-1 inline-block align-middle`}>
      <span style={{ minHeight: '72px' }} className={'inline-block text-center text-secondary small'}>
        Loading cluster<br />
        <span className={'small'}>{clusterId}</span>...
      </span>
    </span>;
  }
}

// Memoized: in the clusters CRUD table one of these renders per row (hundreds of rows, each
// with many thumbnails). Parent state changes (selection, previewSize, hover) re-render the
// whole table; React.memo skips rows whose props (clusterId, size, sortByYear, highlight*,
// preloadedCluster) are referentially unchanged. Callers must keep those props stable.
export default React.memo(ImageClusterPreview);
